import React, { useEffect, useMemo, useState } from 'react';
import {
  clearChronoRelations,
  replaceChronoRelation,
  type ChronoBundle,
  type ChronoRelation,
} from '../../services/chronoService';
import {
  formatChronoNumber,
  observationStats,
  representativeSeconds,
  taktSeconds,
  type ElementNature,
} from '../../utils/chronoStats';
import { scheduleCombinedWork, type RelationKind, type ScheduleElement } from '../../utils/chronoSchedule';

const NATURE_LABEL: Record<ElementNature, string> = {
  manual: 'Manual',
  machine: 'Máquina',
  walking: 'Caminhada',
  waiting: 'Espera',
};

const NATURE_COLOR: Record<ElementNature, string> = {
  manual: '#1d4ed8',
  machine: '#0f766e',
  walking: '#b45309',
  waiting: '#64748b',
};

const LABEL = 128;
const ROW = 46;

function secondsOf(bundle: ChronoBundle, elementId: string): number | null {
  const element = bundle.elements.find((item) => item.id === elementId);
  if (!element) return null;
  const samples = bundle.observations.filter((item) => item.elementId === elementId).map((item) => item.seconds);
  return representativeSeconds(element.representativeMethod, element.representativeSeconds, observationStats(samples));
}

export function CombinedWorkPane({
  bundle,
  onSaved,
  onError,
}: {
  bundle: ChronoBundle;
  onSaved: () => void;
  onError: (error: unknown) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(bundle.elements[0]?.id ?? null);
  const [scale, setScale] = useState(8);
  const [kind, setKind] = useState<RelationKind>('precedes');
  const [fromId, setFromId] = useState('');
  const [lag, setLag] = useState('0');
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);

  const takt = taktSeconds(bundle.study.availableSeconds, bundle.study.demandQuantity);
  const resources = useMemo(
    () => [...bundle.resources].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name)),
    [bundle.resources],
  );
  const elements = useMemo<ScheduleElement[]>(() => bundle.elements.map((element) => ({
    id: element.id,
    resourceId: element.resourceId,
    sortOrder: element.sortOrder,
    duration: secondsOf(bundle, element.id),
    nature: element.nature,
    description: element.description,
  })), [bundle]);

  const schedule = useMemo(
    () => scheduleCombinedWork(
      elements,
      bundle.relations.map((relation) => ({
        id: relation.id,
        fromId: relation.fromElementId,
        toId: relation.toElementId,
        kind: relation.relation,
        lag: relation.lagSeconds,
      })),
      takt,
    ),
    [elements, bundle.relations, takt],
  );

  useEffect(() => {
    const current = bundle.relations.find((relation) => relation.toElementId === selectedId);
    setKind(current?.relation ?? 'precedes');
    setFromId(current?.fromElementId ?? '');
    setLag(current ? String(current.lagSeconds) : '0');
  }, [selectedId, bundle.relations]);

  const selected = bundle.elements.find((element) => element.id === selectedId) ?? null;
  const selectedRelation = bundle.relations.find((relation) => relation.toElementId === selectedId) ?? null;
  const barOf = (id: string) => (schedule.ok ? schedule.bars.find((bar) => bar.id === id) ?? null : null);

  const horizon = schedule.ok
    ? Math.max(schedule.diagnostics.cycle ?? 0, takt ?? 0, 1)
    : Math.max(takt ?? 0, 1);
  const chartWidth = LABEL + horizon * scale + 28;
  const operators = resources.filter((resource) => resource.kind === 'operator').length;
  const machines = resources.filter((resource) => resource.kind === 'machine').length;
  const bottleneck = schedule.ok
    ? resources.find((resource) => resource.id === schedule.diagnostics.bottleneckId) ?? null
    : null;

  const applyRelation = async () => {
    if (!selected) return;
    const origin = bundle.elements.find((element) => element.id === fromId);
    if (!origin) {
      onError(new Error('Escolha o elemento de referência.'));
      return;
    }
    if (origin.scenarioId !== bundle.currentScenarioId || selected.scenarioId !== bundle.currentScenarioId) {
      onError(new Error('A relação usa elemento de outro cenário.'));
      return;
    }
    const lagSeconds = Number(lag.replace(',', '.'));
    if (!Number.isFinite(lagSeconds)) {
      onError(new Error('Informe o deslocamento em segundos.'));
      return;
    }
    const nextRelations: ChronoRelation[] = [
      ...bundle.relations.filter((relation) => relation.toElementId !== selected.id),
      {
        id: 'draft',
        scenarioId: bundle.currentScenarioId,
        fromElementId: origin.id,
        toElementId: selected.id,
        relation: kind,
        lagSeconds,
      },
    ];
    const preview = scheduleCombinedWork(
      elements,
      nextRelations.map((relation) => ({
        id: relation.id,
        fromId: relation.fromElementId,
        toId: relation.toElementId,
        kind: relation.relation,
        lag: relation.lagSeconds,
      })),
      takt,
    );
    if (!preview.ok) {
      onError(new Error(preview.error));
      return;
    }
    setSaving(true);
    try {
      await replaceChronoRelation({
        organizationId: bundle.study.organizationId,
        studyId: bundle.study.id,
        scenarioId: bundle.currentScenarioId,
        toElementId: selected.id,
        fromElementId: origin.id,
        relation: kind,
        lagSeconds,
      });
      onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  const clearRelation = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await clearChronoRelations(bundle.currentScenarioId, selected.id);
      onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  const openElement = (id: string) => {
    setSelectedId(id);
    const current = bundle.relations.find((relation) => relation.toElementId === id);
    setKind(current?.relation ?? 'precedes');
    setFromId(current?.fromElementId ?? '');
    setLag(current ? String(current.lagSeconds) : '0');
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <div className="grid grid-cols-5 gap-1 text-[11px]">
        <Metric label="Takt" value={takt == null ? '—' : `${formatChronoNumber(takt, 1)} s`} />
        <Metric label="Ciclo" value={schedule.ok && schedule.diagnostics.cycle != null ? `${formatChronoNumber(schedule.diagnostics.cycle, 3)} s` : '—'} />
        <Metric label="Gargalo" value={bottleneck?.name ?? '—'} />
        <Metric label="Operadores" value={String(operators)} />
        <Metric label="Máquinas" value={String(machines)} />
      </div>
      {schedule.ok && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-slate-300">
          <span>Manual {formatChronoNumber(schedule.diagnostics.byNature.manual, 3)} s</span>
          <span>Máquina {formatChronoNumber(schedule.diagnostics.byNature.machine, 3)} s</span>
          <span>Caminhada {formatChronoNumber(schedule.diagnostics.byNature.walking, 3)} s</span>
          <span>Espera {formatChronoNumber(schedule.diagnostics.byNature.waiting, 3)} s</span>
          {schedule.diagnostics.resources.map((load) => {
            const resource = resources.find((item) => item.id === load.resourceId);
            return (
              <span key={load.resourceId}>
                {resource?.name}: carga {formatChronoNumber(load.busy, 3)} s · ociosidade {formatChronoNumber(load.idle, 3)} s
                {load.utilization != null ? ` · ${formatChronoNumber(load.utilization * 100, 0)}% do takt` : ''}
              </span>
            );
          })}
          {schedule.diagnostics.unscheduled.length > 0 && (
            <span>Sem tempo: {schedule.diagnostics.unscheduled.map((id) => bundle.elements.find((element) => element.id === id)?.description).filter(Boolean).join(', ')}</span>
          )}
          {schedule.diagnostics.elementsAboveTakt.length > 0 && (
            <span>
              Acima do takt: {schedule.diagnostics.elementsAboveTakt.map((id) => bundle.elements.find((element) => element.id === id)?.description).filter(Boolean).join(', ')}
            </span>
          )}
        </div>
      )}
      {!schedule.ok && <p className="text-xs text-red-700">{schedule.error}</p>}
      <div className="flex min-h-0 min-w-0 flex-1 gap-2">
        <div className="min-w-0 flex-1 overflow-hidden border border-slate-300 dark:border-slate-600">
          <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1 text-[11px] dark:border-slate-700">
            <div className="flex gap-2">
              {(Object.keys(NATURE_LABEL) as ElementNature[]).map((nature) => (
                <span key={nature} className="inline-flex items-center gap-1">
                  <i className="inline-block h-2 w-3" style={{ background: NATURE_COLOR[nature] }} />
                  {NATURE_LABEL[nature]}
                </span>
              ))}
            </div>
            <div className="flex gap-1">
              <button type="button" className="border border-slate-300 px-1" onClick={() => setScale((value) => Math.max(3, value - 2))}>−</button>
              <button type="button" className="border border-slate-300 px-1" onClick={() => setScale((value) => Math.min(28, value + 2))}>+</button>
            </div>
          </div>
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 230px)' }}>
            <div className="relative" style={{ width: chartWidth, height: resources.length * ROW + 22 }}>
              {resources.map((resource, index) => (
                <div key={resource.id} className="absolute left-0 right-0 border-b border-slate-100 dark:border-slate-800" style={{ top: index * ROW, height: ROW }}>
                  <div className="absolute inset-y-0 left-0 flex items-center truncate px-1 text-[11px]" style={{ width: LABEL }}>
                    {resource.name}
                  </div>
                </div>
              ))}
              {takt != null && (
                <div className="absolute bottom-0 top-0 border-l border-dashed border-red-600" style={{ left: LABEL + takt * scale }} title={`TAKT ${formatChronoNumber(takt, 1)} s`}>
                  <span className="absolute -top-0 left-1 text-[10px] font-semibold text-red-700">TAKT</span>
                </div>
              )}
              {schedule.ok && resources.map((resource) => {
                const row = resources.findIndex((item) => item.id === resource.id);
                return schedule.bars.filter((bar) => bar.resourceId === resource.id).map((bar) => {
                  const element = bundle.elements.find((item) => item.id === bar.id);
                  if (!element) return null;
                  const width = Math.max(bar.duration * scale, 3);
                  return (
                    <button
                      key={bar.id}
                      type="button"
                      className={`absolute overflow-hidden px-1 text-left text-[10px] leading-tight text-white ${selectedId === bar.id ? 'outline outline-2 outline-slate-900' : ''}`}
                      style={{
                        left: LABEL + bar.start * scale,
                        top: row * ROW + 10,
                        width,
                        height: 26,
                        background: NATURE_COLOR[element.nature],
                      }}
                      onClick={() => openElement(bar.id)}
                      onMouseEnter={(event) => setHover({ id: bar.id, x: event.clientX, y: event.clientY })}
                      onMouseLeave={() => setHover(null)}
                    >
                      {width > 56 ? element.description : ''}
                    </button>
                  );
                });
              })}
              {schedule.ok && (
                <svg className="pointer-events-none absolute inset-0" width={chartWidth} height={resources.length * ROW + 22}>
                  {bundle.relations.map((relation) => {
                    const from = barOf(relation.fromElementId);
                    const to = barOf(relation.toElementId);
                    if (!from || !to || from.resourceId === to.resourceId) return null;
                    const fromRow = resources.findIndex((resource) => resource.id === from.resourceId);
                    const toRow = resources.findIndex((resource) => resource.id === to.resourceId);
                    const x1 = LABEL + (relation.relation === 'simultaneous' ? from.start : from.end) * scale;
                    const y1 = fromRow * ROW + 23;
                    const x2 = LABEL + to.start * scale;
                    const y2 = toRow * ROW + 23;
                    return <line key={relation.id} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0f172a" strokeWidth="1" />;
                  })}
                </svg>
              )}
            </div>
          </div>
        </div>
        <aside className="w-64 shrink-0 border border-slate-300 p-2 text-[11px] dark:border-slate-600">
          {!selected && <p>Selecione um elemento na linha do tempo.</p>}
          {selected && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold">{selected.description}</p>
              <p>Seq. {selected.sortOrder}</p>
              <p>Recurso {resources.find((resource) => resource.id === selected.resourceId)?.name}</p>
              <p>{NATURE_LABEL[selected.nature]} · {selected.valueClass}</p>
              <p>Duração {formatChronoNumber(secondsOf(bundle, selected.id), 3)} s</p>
              <p>Início {formatChronoNumber(barOf(selected.id)?.start ?? null, 3)} s</p>
              <p>Fim {formatChronoNumber(barOf(selected.id)?.end ?? null, 3)} s</p>
              <label className="flex flex-col gap-1">
                Ligação
                <select className="border border-slate-300 bg-white px-1 py-1 dark:bg-slate-900" value={kind} onChange={(event) => setKind(event.target.value as RelationKind)}>
                  <option value="precedes">Começa após</option>
                  <option value="simultaneous">Começa junto com</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                Elemento
                <select className="border border-slate-300 bg-white px-1 py-1 dark:bg-slate-900" value={fromId} onChange={(event) => setFromId(event.target.value)}>
                  <option value="">Escolher</option>
                  {bundle.elements.filter((element) => element.id !== selected.id).map((element) => (
                    <option key={element.id} value={element.id}>{element.description}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                Deslocamento (s)
                <input className="border border-slate-300 px-1 py-1 dark:bg-slate-900" value={lag} onChange={(event) => setLag(event.target.value)} />
              </label>
              <div className="flex gap-1">
                <button type="button" disabled={saving} className="border border-slate-900 px-2 py-1 dark:border-slate-100" onClick={applyRelation}>Aplicar</button>
                <button type="button" disabled={saving || !selectedRelation} className="border border-slate-300 px-2 py-1" onClick={clearRelation}>Limpar</button>
              </div>
              <p className="text-slate-500">O início e o fim são calculados aqui e não são gravados.</p>
            </div>
          )}
        </aside>
      </div>
      {hover && schedule.ok && (() => {
        const element = bundle.elements.find((item) => item.id === hover.id);
        const bar = barOf(hover.id);
        const resource = resources.find((item) => item.id === element?.resourceId);
        if (!element || !bar) return null;
        return (
          <div className="pointer-events-none fixed z-50 border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-white" style={{ left: hover.x + 12, top: hover.y + 12 }}>
            <div>{element.description} · seq. {element.sortOrder}</div>
            <div>{resource?.name} · {NATURE_LABEL[element.nature]} · {element.valueClass}</div>
            <div>Início {formatChronoNumber(bar.start, 3)} · Fim {formatChronoNumber(bar.end, 3)} · {formatChronoNumber(bar.duration, 3)} s</div>
          </div>
        );
      })()}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-slate-300 px-2 py-1 dark:border-slate-600">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="truncate text-sm font-semibold">{value}</div>
    </div>
  );
}
